import { useCallback } from 'react';

import { useSetState } from '@upside/ui-hooks-lib';

import { Content } from 'components/Layout';
import { LoginForm } from 'components/Login/LoginForm';
import { MultiFactor } from 'components/Login/MultiFactor';
import { Register } from 'components/Login/Register';
import { ResetRequired } from 'components/Login/ResetRequired';
import { TotpVerify } from 'components/Login/TotpVerify';

import { TotpSetup } from './TotpSetup';
import { useIdpRedirect } from './hooks/useIdpRedirect';
import {
  LoginState,
  MFADetails,
  RegisterState,
  ResetRequiredState,
  TotpDetails,
  TotpSetupDetails,
} from './types';

type LoginPageState = {
  timedOut: boolean;
  mfaState?: MFADetails;
  totpState?: TotpDetails;
  totpSetupState?: TotpSetupDetails;
  registerState?: RegisterState;
  resetRequiredState?: ResetRequiredState;
  pageState: LoginState;
};

export function Login() {
  const { isDiscovering } = useIdpRedirect();

  const [
    {
      timedOut,
      mfaState,
      totpState,
      totpSetupState,
      registerState,
      resetRequiredState,
      pageState,
    },
    setLoginPageState,
  ] = useSetState<LoginPageState>({
    timedOut: false,
    mfaState: undefined,
    totpState: undefined,
    totpSetupState: undefined,
    registerState: undefined,
    resetRequiredState: undefined,
    pageState: LoginState.LOGIN,
  });

  const handleTimeout = useCallback(() => {
    setLoginPageState({ timedOut: true, pageState: LoginState.LOGIN });
  }, [setLoginPageState]);

  const setTotpState = (totpDetails: TotpDetails) => {
    setLoginPageState({
      pageState: LoginState.SOFTWARE_MFA,
      totpState: totpDetails,
      timedOut: false,
    });
  };

  if (isDiscovering) {
    return <Content loading />;
  }

  switch (pageState) {
    case LoginState.MFA:
      return (
        <MultiFactor
          handleCancel={() => {
            setLoginPageState({ pageState: LoginState.LOGIN });
          }}
          handleTimeout={handleTimeout}
          mfaState={mfaState}
        />
      );
    case LoginState.SOFTWARE_MFA:
      return (
        <TotpVerify
          handleCancel={() => {
            setLoginPageState({ pageState: LoginState.LOGIN });
          }}
          handleTimeout={handleTimeout}
          totpState={totpState}
        />
      );
    case LoginState.SOFTWARE_MFA_SETUP:
      return (
        <TotpSetup
          handleCancel={() => {
            setLoginPageState({ pageState: LoginState.LOGIN });
          }}
          handleTimeout={handleTimeout}
          totpSetupState={
            totpSetupState ?? { email: '', sessionId: '', secretCode: '' }
          }
          setTotpState={setTotpState}
        />
      );
    case LoginState.REGISTER:
      return (
        <Register
          email={registerState?.email || ''}
          sessionId={registerState?.sessionId || ''}
        />
      );
    case LoginState.RESET_REQUIRED:
      return (
        <ResetRequired
          email={resetRequiredState?.email || ''}
          backToLogin={() => {
            setLoginPageState({ pageState: LoginState.LOGIN });
          }}
        />
      );
    case LoginState.LOGIN:
    default:
      return (
        <LoginForm
          timedOut={timedOut}
          setRegisterState={(registerDetails: RegisterState) => {
            setLoginPageState({
              pageState: LoginState.REGISTER,
              registerState: registerDetails,
            });
          }}
          setMfaState={(mfaDetails: MFADetails) => {
            setLoginPageState({
              pageState: LoginState.MFA,
              mfaState: mfaDetails,
              timedOut: false,
            });
          }}
          setTotpState={setTotpState}
          setTotpSetupState={(totpSetupDetails: TotpSetupDetails) => {
            setLoginPageState({
              pageState: LoginState.SOFTWARE_MFA_SETUP,
              totpSetupState: totpSetupDetails,
              timedOut: false,
            });
          }}
          setResetRequiredState={(resetRequiredDetails: ResetRequiredState) => {
            setLoginPageState({
              pageState: LoginState.RESET_REQUIRED,
              resetRequiredState: resetRequiredDetails,
            });
          }}
        />
      );
  }
}
